import type {
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';

import { normalizePk } from './transform';

/**
 * `register` para un campo de PK: igual que el de react-hook-form, pero al
 * salir del campo deja el valor en la forma canónica (`90+50` → `90 + 050`).
 *
 * Por qué normalizar en vez de exigir 3 dígitos: los metros son metros, así
 * que `50` son 50 m. Aceptarlo callado sería peligroso (quien quería 500 m
 * guardaría 50 sin enterarse), de ahí que se reescriba el campo — el usuario
 * ve inmediatamente cómo se ha interpretado lo que escribió.
 *
 * Si el texto no es un PK, no se toca: habla la validación del schema.
 *
 * Uso: `<InputText {...registerPk(register, setValue, 'pkInit')} />`
 */
export function registerPk<T extends FieldValues, N extends Path<T>>(
  register: UseFormRegister<T>,
  setValue: UseFormSetValue<T>,
  name: N
) {
  const field = register(name);

  return {
    ...field,
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
      const normalized = normalizePk(event.target.value);
      if (normalized && normalized !== event.target.value) {
        setValue(name, normalized as PathValue<T, N>, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return field.onBlur(event);
    },
  };
}
